import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import { ReactElement } from 'react';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';

import { getCategoryFacetLink } from '@/utils/urls';

type Siblings = {
    [key: string]: any;
    landingPageUrl?: string;
    name: string;
    selected: boolean;
};

type selectedSiblingChildren = {
    [key: string]: any;
    landingPageUrl?: string;
    name: string;
};

export type TaxonomySiblingProps = {
    queryParams: any;
    selectedSiblingChildren: selectedSiblingChildren[];
    siblings: Siblings[];
};

const TaxonomySibling = ({
    queryParams,
    selectedSiblingChildren = [],
    siblings = [],
}: TaxonomySiblingProps): ReactElement => {
    if (siblings.length === 0) {
        return null;
    }

    const alphabetize = <T extends { name: string }>(list: T[]): T[] => {
        // only sort if there are 2 or more elements
        if (!(list?.length > 1)) {
            return list;
        }
        return list.sort((a, b) => (a.name > b.name ? 1 : -1));
    };
    const sortedSiblings = alphabetize(siblings);
    const sortedSelectedSiblingChildren = alphabetize(selectedSiblingChildren);

    return (
        <StyledSiblingContainer>
            {sortedSiblings.map((sibling) => {
                return sibling.selected ? (
                    <div key={sibling.id}>
                        <StyledSelectedSibling color="secondary">{sibling.name}</StyledSelectedSibling>
                        {sortedSelectedSiblingChildren?.map((child) => {
                            return (
                                <StyledChildSibling href={getCategoryFacetLink(child.landingPageUrl, queryParams)}>
                                    <BodySecondary color="secondary">{child.name}</BodySecondary>
                                </StyledChildSibling>
                            );
                        })}
                    </div>
                ) : (
                    <div key={sibling.id}>
                        {sibling.landingPageUrl && (
                            <StyledSibling href={getCategoryFacetLink(sibling.landingPageUrl, queryParams)}>
                                <BodySecondary color="secondary">{sibling.name}</BodySecondary>
                            </StyledSibling>
                        )}
                    </div>
                );
            })}
        </StyledSiblingContainer>
    );
};

export default React.memo<TaxonomySiblingProps>(TaxonomySibling, isEqual);

const StyledSiblingContainer = styled.div`
    margin-top: ${({ theme }) => theme.spacing.xs};
`;

const StyledSibling = styled(Link)`
    text-decoration: none;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StyledChildSibling = styled(Link)`
    text-decoration: none;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    margin-left: ${({ theme }) => theme.spacing.md};
`;

const StyledSelectedSibling = styled(BodySecondary)`
    display: inline-block;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    font-weight: bold;
`;
